import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, FileText, Loader2, PenLine, ShieldCheck } from "lucide-react";
import SEO from "@/components/site/SEO";
import { getPricingConfig, quoteEstimate, signAgreement, submitLead } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";

const SERVICES = [
  "Business Website", "One-Page Website", "Landing Page", "E-commerce Website",
  "Booking Website", "Website Redesign", "Custom Web Application", "SEO & Visibility Only",
];
const BUDGETS = ["Under $1,000", "$1,000 – $2,500", "$2,500 – $5,000", "$5,000+", "Not sure yet"];
const COUNTRIES = ["United States", "United Kingdom", "Canada", "Australia", "New Zealand", "Ireland", "France", "Germany", "Netherlands", "Belgium", "Switzerland", "UAE", "Singapore", "South Africa", "Other"];

const STEPS = ["Service", "Business", "Features", "Budget", "Contact", "Plan", "Agreement", "Done"];

const inputCls = "w-full bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary text-foreground";

function Option({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
      className={`text-left border px-4 py-3.5 transition-all active:scale-[0.98] w-full ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"}`}>
      <span className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">{children}</span>
        {active && <Check className="w-4 h-4 text-primary" />}
      </span>
    </button>
  );
}

export default function StartProject() {
  const location = useLocation();
  const [params] = useSearchParams();
  const { fmt, code } = useCurrency();
  const prefill = location.state || {};
  const isProposal = prefill.mode === "proposal";

  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => {
    const s = prefill.selections || {};
    return {
      service: "Business Website",
      business_type: s.business_type || "small-business",
      business_desc: prefill.description || "",
      size: s.size || "small",
      features: s.features || [],
      design: s.design || "professional",
      budget: "",
      name: "", email: "", company: "", country: "United States",
    };
  });
  const [estimate, setEstimate] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [financing, setFinancing] = useState(false);
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);

  useEffect(() => { getPricingConfig().then(setCfg).catch(() => {}); }, []);

  // Package deep-link (?package=business) presets size.
  useEffect(() => {
    const pkg = params.get("package");
    if (!pkg || !cfg) return;
    const sizeMap = { starter: "one-page", business: "small", "business-pro": "medium", custom: "large" };
    if (sizeMap[pkg]) setForm((f) => ({ ...f, size: sizeMap[pkg] }));
    if (params.get("cancelled")) toast.info("Checkout cancelled — your answers are saved, pick up where you left off.");
  }, [params, cfg]);

  // Compute the estimate server-side when reaching the plan step.
  useEffect(() => {
    if (step !== 5 || estimate) return;
    quoteEstimate({
      business_type: form.business_type, size: form.size, features: form.features,
      design: form.design,
    }).then(setEstimate).catch(() => toast.error("Could not compute the estimate — please try again."));
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFeature = (k) =>
    setForm((f) => ({ ...f, features: f.features.includes(k) ? f.features.filter((x) => x !== k) : [...f.features, k] }));

  const canNext = useMemo(() => {
    if (!cfg) return false;
    if (step === 2) return true; // features optional
    if (step === 3) return !!form.budget;
    if (step === 4) return form.name.trim() && /.+@.+\..+/.test(form.email);
    return true;
  }, [step, form, cfg]);

  const monthlyCharge = estimate ? estimate.lease_monthly + (financing ? estimate.finance_monthly : 0) : 0;
  const firstChargeDate = useMemo(() => {
    const d = new Date(Date.now() + 30 * 86400000);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  }, []);

  const signAndCheckout = async () => {
    if (!estimate) return;
    if (signature.trim().length < 3) { toast.error("Type your full legal name to sign."); return; }
    if (!agreed) { toast.error("Please confirm you've read the agreement."); return; }
    setBusy(true);
    try {
      const r = await signAgreement({
        estimate_id: estimate.id,
        service: form.service, business_desc: form.business_desc,
        name: form.name, email: form.email, company: form.company, country: form.country,
        signature: signature.trim(), billing, financing,
        currency: code, origin_url: window.location.origin,
      });
      window.location.href = r.checkout_url;
    } catch {
      toast.error("Could not open secure billing — please try again.");
      setBusy(false);
    }
  };

  const requestProposal = async () => {
    setBusy(true);
    try {
      await submitLead({
        type: "quote", name: form.name, email: form.email, company: form.company,
        country: form.country, industry: form.business_type, service: form.service,
        budget: form.budget, message: form.business_desc,
        estimate: estimate ? { total: estimate.total, monthly: estimate.lease_monthly } : undefined,
      });
      setStep(7);
    } catch {
      toast.error("Could not send the request — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const stepBody = () => {
    if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
    switch (step) {
      case 0:
        return (
          <div className="grid sm:grid-cols-2 gap-2">
            {SERVICES.map((s) => (
              <Option key={s} testId={`sp-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} active={form.service === s}
                onClick={() => setForm((f) => ({ ...f, service: s }))}>{s}</Option>
            ))}
          </div>
        );
      case 1:
        return (
          <div className="space-y-5">
            <div>
              <label className="overline text-muted-foreground block mb-2">Business type</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {cfg.business_types.map((b) => (
                  <Option key={b.key} testId={`sp-type-${b.key}`} active={form.business_type === b.key}
                    onClick={() => setForm((f) => ({ ...f, business_type: b.key }))}>{b.label}</Option>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="sp-desc" className="overline text-muted-foreground block mb-2">Tell me about your business and what the website needs to do</label>
              <textarea id="sp-desc" rows={4} value={form.business_desc} data-testid="sp-desc"
                onChange={(e) => setForm((f) => ({ ...f, business_desc: e.target.value }))}
                placeholder="e.g. I'm a consultant in Chicago. I need a site that builds trust and lets prospects book a call…"
                className={inputCls} />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {cfg.features.map((f) => (
              <Option key={f.key} testId={`sp-feature-${f.key}`} active={form.features.includes(f.key)}
                onClick={() => toggleFeature(f.key)}>{f.label}</Option>
            ))}
          </div>
        );
      case 3:
        return (
          <div className="grid sm:grid-cols-2 gap-2">
            {BUDGETS.map((b) => (
              <Option key={b} testId={`sp-budget-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`} active={form.budget === b}
                onClick={() => setForm((f) => ({ ...f, budget: b }))}>{b}</Option>
            ))}
          </div>
        );
      case 4:
        return (
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label htmlFor="sp-name" className="overline text-muted-foreground block mb-2">Name *</label>
              <input id="sp-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="sp-name" className={inputCls} /></div>
            <div><label htmlFor="sp-email" className="overline text-muted-foreground block mb-2">Email *</label>
              <input id="sp-email" required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="sp-email" className={inputCls} /></div>
            <div><label htmlFor="sp-company" className="overline text-muted-foreground block mb-2">Company</label>
              <input id="sp-company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} data-testid="sp-company" className={inputCls} /></div>
            <div><label htmlFor="sp-country" className="overline text-muted-foreground block mb-2">Country</label>
              <select id="sp-country" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} data-testid="sp-country" className={inputCls}>
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select></div>
          </div>
        );
      case 5:
        if (!estimate) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
        return (
          <div className="space-y-6" data-testid="sp-plan">
            <div className="border border-border bg-card p-6 md:p-8">
              <p className="overline text-primary mb-5">Your project</p>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-border pb-3"><span className="text-muted-foreground">Service</span><span className="font-medium">{form.service}</span></div>
                <div className="flex justify-between border-b border-border pb-3"><span className="text-muted-foreground">Website build ({estimate.size})</span><span className="font-mono">{fmt(estimate.website_build)}</span></div>
                <div className="flex justify-between border-b border-border pb-3"><span className="text-muted-foreground">Features &amp; design</span><span className="font-mono">{fmt(estimate.features_total + estimate.design_add)}</span></div>
                <div className="flex justify-between border-b border-border pb-3"><span className="text-muted-foreground">SEO &amp; launch setup</span><span className="font-mono">{fmt(estimate.seo_setup)}</span></div>
                <div className="flex justify-between items-end pt-2">
                  <span className="font-medium">Project value</span>
                  <span className="font-display text-4xl text-primary" data-testid="sp-total">{fmt(estimate.total)}</span>
                </div>
              </div>
            </div>

            <div className="border border-primary/40 bg-primary/5 p-6 md:p-8" data-testid="sp-lease">
              <p className="overline text-primary mb-2">How you pay</p>
              <p className="font-display text-2xl mb-1">Free for 30 days. Then one simple plan.</p>
              <p className="text-sm text-muted-foreground mb-6">I build and launch your site now — you pay nothing today. If it's not right, cancel before {firstChargeDate} and you owe nothing.</p>

              <div className="grid grid-cols-2 gap-1 border border-border bg-background p-1 mb-4 max-w-md" data-testid="sp-billing-toggle">
                <button onClick={() => setBilling("monthly")} data-testid="sp-billing-monthly"
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${billing === "monthly" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  Monthly
                </button>
                <button onClick={() => { setBilling("annual"); setFinancing(false); }} data-testid="sp-billing-annual"
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${billing === "annual" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  Annual — 2 months free
                </button>
              </div>

              {billing === "monthly" && (
                <label className="flex items-start gap-3 border border-border bg-background p-4 mb-4 cursor-pointer max-w-md" data-testid="sp-financing">
                  <input type="checkbox" checked={financing} onChange={(e) => setFinancing(e.target.checked)} className="mt-1 accent-[#FF4500]" data-testid="sp-financing-check" />
                  <span className="text-sm">
                    <b>Finance the build over 12 months</b> — add {fmt(estimate.finance_monthly)}/mo for the first year, then your plan drops to {fmt(estimate.lease_monthly)}/mo. After 12 payments the build is fully yours.
                  </span>
                </label>
              )}

              <div className="flex items-end justify-between border-t border-border pt-4 max-w-md">
                <span className="text-sm text-muted-foreground">From {firstChargeDate}</span>
                <span className="font-display text-3xl text-primary" data-testid="sp-charge">
                  {billing === "monthly" ? `${fmt(monthlyCharge)}/mo` : `${fmt(estimate.lease_annual)}/yr`}
                </span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground max-w-md">
                Includes hosting, security, backups, monitoring, updates and care of everything we build. Cancel anytime by email.
              </p>
            </div>
          </div>
        );
      case 6:
        if (!estimate) return null;
        return (
          <div className="space-y-6" data-testid="sp-agreement">
            <div className="border border-border bg-card p-6 md:p-8 max-h-[380px] overflow-y-auto" data-testid="sp-agreement-text">
              <p className="overline text-primary mb-4">Service Agreement</p>
              <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
                <p><b className="text-foreground">1. The work.</b> Julius Mason will design, build and launch your {form.service} — {estimate.size}, {estimate.design} style{estimate.features.length ? `, with ${estimate.features.map((f) => f.label).join(", ")}` : ""}. I build and launch during your free month.</p>
                <p><b className="text-foreground">2. Free first month.</b> You pay nothing today. If you cancel before {firstChargeDate}, nothing is owed.</p>
                <p><b className="text-foreground">3. Your plan.</b> From {firstChargeDate}: <b className="text-foreground">{billing === "monthly" ? `${fmt(monthlyCharge)}/month` : `${fmt(estimate.lease_annual)}/year`}</b> — hosting, security, backups, monitoring, updates and ongoing care included.{financing ? ` The first 12 payments include ${fmt(estimate.finance_monthly)}/month of build financing (project value ${fmt(estimate.total)}); after that your plan drops to ${fmt(estimate.lease_monthly)}/month.` : ""}</p>
                <p><b className="text-foreground">4. Cancellation.</b> Cancel anytime by email — no fees, no notice period. Your site stays live until the end of the paid period; you can request a full export of your content anytime.</p>
                <p><b className="text-foreground">5. Ownership.</b> Your content and brand are always yours. The website is licensed to you while your plan is active{financing ? "; after 12 financing payments the build is fully yours" : ""}.</p>
                <p><b className="text-foreground">6. Fair use.</b> Your plan covers upkeep and small content updates (~30 min/month). Bigger changes are quoted separately — always approved by you in writing first.</p>
              </div>
            </div>

            <div>
              <label htmlFor="sp-signature" className="overline text-muted-foreground block mb-2">Sign with your full legal name *</label>
              <input id="sp-signature" value={signature} onChange={(e) => setSignature(e.target.value)}
                data-testid="sp-signature" placeholder={form.name || "Your full legal name"}
                className={inputCls + " font-display text-xl italic"} />
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="sp-agree-wrap">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 accent-[#FF4500]" data-testid="sp-agree" />
              <span className="text-sm text-muted-foreground">I've read the agreement and I understand billing starts on {firstChargeDate}. I can cancel free of charge before then.</span>
            </label>

            <button onClick={signAndCheckout} disabled={busy || !agreed || signature.trim().length < 3}
              data-testid="sp-sign-checkout"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-50">
              <PenLine className="w-4 h-4" /> {busy ? "Opening secure billing…" : "Sign & Add Billing Details"}
            </button>
            <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Card on file via Stripe · $0 charged today · PDF copy emailed to you
            </p>
            <button onClick={requestProposal} disabled={busy} data-testid="sp-proposal-instead"
              className="mx-auto block text-sm text-muted-foreground hover:text-primary link-underline">
              Prefer a detailed written proposal first?
            </button>
          </div>
        );
      case 7:
        return (
          <div className="border border-primary/40 bg-primary/5 p-8 md:p-10 text-center" data-testid="sp-proposal-sent">
            <FileText className="w-10 h-10 text-primary mx-auto mb-4" />
            <p className="font-display text-3xl">Your request is in.</p>
            <p className="mt-4 text-muted-foreground max-w-md mx-auto">
              I'll review your brief and email a detailed proposal to <b className="text-foreground">{form.email}</b> within 1–2 business days.
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 text-primary link-underline font-medium">Back to the homepage <ArrowRight className="w-4 h-4" /></Link>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="pt-28 md:pt-36 pb-24 min-h-screen" data-testid="start-project-page">
      <SEO
        title="Start My Project — Free for 30 Days | Julius Mason"
        description="Start your website project online in minutes: describe your business, review your plan, sign the agreement. Free for 30 days — billing only starts after your site is live."
        path="/start-project"
        lang="en"
        noindex
      />
      <div className="mx-auto max-w-3xl px-5 md:px-8">
        <p className="overline text-primary mb-4">Start My Project</p>
        <h1 className="font-display text-4xl md:text-5xl leading-tight mb-4">
          {isProposal ? "Request your detailed proposal." : "Let's get your project booked."}
        </h1>
        <p className="text-muted-foreground mb-10">A few quick questions — then your plan and a simple agreement. <b className="text-foreground">Free for the first 30 days</b>; billing only starts once your site is live. Takes about two minutes.</p>

        {/* Progress */}
        <div className="flex items-center gap-1.5 mb-10" data-testid="sp-progress">
          {STEPS.slice(0, 7).map((s, i) => (
            <div key={s} className={`h-1 flex-1 transition-colors ${i <= step ? "bg-primary" : "bg-border"}`} title={s} />
          ))}
        </div>
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
          Step {Math.min(step + 1, 7)} of 7 — {STEPS[Math.min(step, 6)]}
        </p>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            {stepBody()}
          </motion.div>
        </AnimatePresence>

        {step <= 5 && (
          <div className="mt-10 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
              data-testid="sp-back"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext}
              data-testid="sp-next"
              className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-40">
              {step === 5 ? (isProposal ? "Review → Request Proposal" : "Continue to Agreement") : "Continue"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {step === 6 && (
          <button onClick={() => setStep(5)} className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition" data-testid="sp-back-plan">
            <ArrowLeft className="w-4 h-4" /> Back to plan
          </button>
        )}
      </div>
    </div>
  );
}
